import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from '../../components/organisms';
import { Button } from '../../components/atoms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { TrailerFormModal } from '../../components/organisms/TrailerFormModal';
import { useTrailers, useTrailerActions } from '../../hooks/useTrailers';
import type { Column, TableAction } from '../../types/table';
import type { Trailer, TrailerCreateRequest, TrailerUpdateRequest } from '../../types/api/trailer';
import { formatDateForDisplay } from '../../types/api/drama';
import {
  formatLinkType,
  formatTargetAudience,
  getLinkTypeBadgeColor,
  getTargetAudienceBadgeColor,
} from '../../types/api/banner';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';

/**
 * TrailerManagementPage Component
 *
 * Admin page for managing trailer videos:
 * - Server-side pagination
 * - Keyword search
 * - CRUD operations (create, edit, delete, activate, deactivate)
 * - Video and thumbnail preview
 * - Link type and target audience badges
 */
export const TrailerManagementPage: React.FC = () => {
  const { fetchTrailers } = useTrailers();
  const {
    createTrailerWithFiles,
    createTrailerWithJson,
    updateTrailerWithFiles,
    updateTrailerWithJson,
    deleteTrailer,
    activateTrailer,
    deactivateTrailer,
  } = useTrailerActions();
  const [refreshKey, setRefreshKey] = useState(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState<Trailer | null>(null);

  const handleRefresh = useCallback(() => {
    // Increment key to force remount, which will trigger fresh data fetch
    setRefreshKey(prev => prev + 1);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedTrailer(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setIsDeleteModalOpen(true);
  }, []);

  const handleActivate = useCallback(
    async (trailer: Trailer) => {
      try {
        const loadingToast = toast.loading('Activating trailer...');
        await activateTrailer(trailer.trailerId);
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch (error: any) {
        // Error already handled in hook with toast
      }
    },
    [activateTrailer, handleRefresh]
  );

  const handleDeactivate = useCallback(
    async (trailer: Trailer) => {
      try {
        const loadingToast = toast.loading('Deactivating trailer...');
        await deactivateTrailer(trailer.trailerId);
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch (error: any) {
        // Error already handled in hook with toast
      }
    },
    [deactivateTrailer, handleRefresh]
  );

  const handleCreateSubmit = useCallback(
    async (
      data: TrailerCreateRequest | TrailerUpdateRequest,
      videoFile?: File | null,
      thumbnailFile?: File | null
    ) => {
      try {
        if (videoFile && thumbnailFile) {
          await createTrailerWithFiles(
            data as TrailerCreateRequest,
            videoFile,
            thumbnailFile
          );
        } else {
          await createTrailerWithJson(data as TrailerCreateRequest);
        }
        toast.success('Trailer created successfully');
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: any) {
        toast.error(error.message || 'Failed to create trailer');
      }
    },
    [createTrailerWithFiles, createTrailerWithJson, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (
      data: TrailerCreateRequest | TrailerUpdateRequest,
      videoFile?: File | null,
      thumbnailFile?: File | null
    ) => {
      if (!selectedTrailer) return;

      try {
        if (videoFile || thumbnailFile) {
          await updateTrailerWithFiles(
            selectedTrailer.trailerId,
            data as TrailerUpdateRequest,
            videoFile || undefined,
            thumbnailFile || undefined
          );
        } else {
          await updateTrailerWithJson(selectedTrailer.trailerId, data as TrailerUpdateRequest);
        }
        toast.success('Trailer updated successfully');
        setIsEditModalOpen(false);
        setSelectedTrailer(null);
        handleRefresh();
      } catch (error: any) {
        toast.error(error.message || 'Failed to update trailer');
      }
    },
    [selectedTrailer, updateTrailerWithFiles, updateTrailerWithJson, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedTrailer) return;

    try {
      await deleteTrailer(selectedTrailer.trailerId);
      toast.success('Trailer deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedTrailer(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete trailer');
    }
  }, [selectedTrailer, deleteTrailer, handleRefresh]);

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const columns = useMemo<Column<Trailer>[]>(
    () => [
      {
        id: 'trailer',
        header: 'Trailer',
        accessor: 'title',
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-20 h-12 rounded bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {row.thumbnailUrl ? (
                <img
                  src={resolveAssetUrl(row.thumbnailUrl)}
                  alt={row.altText || row.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-xs font-semibold text-gray-600">No thumbnail</span>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm font-semibold text-gray-900 truncate">{row.title}</p>
              {row.description && (
                <p className="text-xs text-gray-500 truncate">{row.description}</p>
              )}
              {row.durationSeconds && (
                <p className="text-xs text-gray-400 truncate mt-0.5">
                  Duration: {formatDuration(row.durationSeconds)}
                </p>
              )}
            </div>
          </div>
        ),
        minWidth: '250px',
      },
      {
        id: 'linkType',
        header: 'Link Type',
        accessor: 'linkType',
        cell: (row) => (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLinkTypeBadgeColor(row.linkType)}`}>
            {formatLinkType(row.linkType)}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'targetAudience',
        header: 'Audience',
        accessor: 'targetAudience',
        cell: (row) => (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTargetAudienceBadgeColor(row.targetAudience)}`}>
            {formatTargetAudience(row.targetAudience)}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'isActive',
        cell: (row) => (
          <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {row.isActive ? 'Active' : 'Inactive'}
            </span>
            {row.isFeatured && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                ⭐ Featured
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'displayOrder',
        header: 'Order',
        accessor: 'displayOrder',
        cell: (row) => (
          <span className="text-sm text-gray-600">{row.displayOrder}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessor: 'createdAt',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {formatDateForDisplay(row.createdAt)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<Trailer>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'toggle-status',
        label: 'Toggle Status',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: (row: Trailer) => (row.isActive ? handleDeactivate(row) : handleActivate(row)),
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleActivate, handleDeactivate, handleDelete]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Trailer Management</h1>
          <p className="text-gray-600 mt-1 pl-3">
            Manage trailer videos with scheduling and targeting
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreate}
            leftIcon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            }
          >
            Add Trailer
          </Button>
        </div>
      </div>

      {/* DataTable */}
      <DataTable<Trailer>
        columns={columns}
        fetchData={fetchTrailers}
        initialPageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Trailer management table"
        key={refreshKey}
        renderHeaderActions={({ setFilter, removeFilter, filters }) => {
          const isActiveFilter = filters.find(f => f.columnId === 'isActive');
          const currentValue = isActiveFilter?.value || '';

          return (
            <div className="flex items-center gap-2">
              <label htmlFor="is-active-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Status:
              </label>
              <select
                id="is-active-filter"
                value={currentValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    removeFilter('isActive');
                  } else {
                    setFilter({
                      columnId: 'isActive',
                      operator: 'equals',
                      value: value,
                    });
                  }
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          );
        }}
      />

      {/* Create Trailer Modal */}
      <TrailerFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      {/* Edit Trailer Modal */}
      <TrailerFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTrailer(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        trailer={selectedTrailer || undefined}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTrailer(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Trailer"
        message={
          selectedTrailer
            ? `Are you sure you want to delete "${selectedTrailer.title}"? This action cannot be undone.`
            : 'Are you sure you want to delete this trailer?'
        }
      />
    </div>
  );
};

export default TrailerManagementPage;
